import { NSARouterMakerResult } from "namirasoft-account-react";
import { ChatInferencerType, FailoverFullRow, FailoverInputRow, FailoverRow, FailoverTargetInputRow, InferencerType } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ChatList from "../components/ChatList";
import InferencerCommonFields from "../components/InferencerCommonFields";
import FailoverTargetLine from "../components/FailoverTargetLine";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerVisualizer from "../components/InferencerVisualizer";
import UsageChart from "../components/UsageChart";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface FailoverEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class FailoverEditPage extends Component<FailoverEditPageProps>
{
    NSRepeater_Targets = createRef<NSRepeater<FailoverTargetLine, FailoverTargetInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<FailoverRow, FailoverInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.failover.edit : InferenceInfo.failover.create }}
                pages={{
                    entity: InferenceEntities.failover(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_view: {
                                tabs: {
                                    extra: {
                                        after_general: [
                                            {
                                                title: "Chats",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <ChatList
                                                        {...this.props}
                                                        inferencer_type={ChatInferencerType.FAILOVER}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                            {
                                                title: "Usage",
                                                clicked: false,
                                                isVisible: (model) => !!model.id,
                                                getContent: (model) => model.id
                                                    ? <UsageChart
                                                        {...this.props}
                                                        inferencer_type={InferencerType.FAILOVER}
                                                        inferencer_id={model.id}
                                                    />
                                                    : null,
                                            },
                                        ],
                                    },
                                },
                            },
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): FailoverInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let targets = this.NSRepeater_Targets.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            targets,
                                            failover_category: [],
                                            failover_field: [],
                                            failover_tag: [],
                                        };
                                    },
                                    setRow: (row: FailoverRow) =>
                                    {
                                        let full = row as FailoverFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerCommonFields_Ids.current?.setValue(full);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Targets.current?.setValue((full.targets ?? []).map(t => ({
                                            inferencer_type: t.inferencer_type,
                                            inferencer_id: t.inferencer_id,
                                            timeout: t.timeout,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Targets",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<FailoverTargetLine, FailoverTargetInputRow>
                                                    ref={this.NSRepeater_Targets}
                                                    title="Targets"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<FailoverTargetLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <FailoverTargetLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`TargetLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: FailoverTargetInputRow | null): boolean
                                                    {
                                                        return !FailoverTargetLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: FailoverTargetLine, checkError: boolean): FailoverTargetInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: FailoverTargetLine, _: number, value: FailoverTargetInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="failover" />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, _row) => id
                                ? <>
                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                    <InferencerLoader
                                        {...this.props}
                                        inferencer_type={InferencerType.FAILOVER}
                                        inferencer_id={id}
                                        render={({ tree, loading, error }) => (
                                            <>
                                                <InferencerGraph tree={tree} loading={loading} error={error} />
                                                <NSSpace size={NSSpaceSizeType.SMALL} />
                                                <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                            </>
                                        )}
                                    />
                                </>
                                : null,
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/failover/#field-name" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/failover/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}